/**
 * 메뉴 열기 지시 — AI Center(v2) 의 OPEN_MENU 를 화면에서 실행한다 (2026-09-13, D47 · D49)
 *
 * 중앙은 "이 메뉴를 열어라(조건이 있으면 채워 조회하라)" 라고만 내려준다. 실행은 여기서.
 *   1. 권한상 메뉴 목록(useMenuStore.menuItems)에서 찾는다. 없으면 열지 않는다 — 목록이 곧 권한이다.
 *   2. 레이아웃이 등록해 둔 전역 메뉴 열기(window.onActivate = handleMenuClick)로 연다.
 *      좌측 메뉴 · 메뉴검색 · 공유 링크가 전부 이 함수로 여니 탭 중복 판정도 그대로 받는다.
 *   3. 조건이 있으면 그 화면의 검색 패널이 등록될 때까지 잠깐 기다린 뒤
 *      applySearchParamsAndRun 으로 채워 조회한다 — 기존 조회 지시와 같은 길이라 행동 신호(D44)도 그대로 난다.
 *
 * v1 은 이 파일을 모른다. v2 응답의 screenOpen 에만 반응한다.
 * 2026-10-02 조건 적용 실패 문구를 이유별로 (중앙 · 화면 가르기). 패널이 칸 이름을 알 때까지 기다린 뒤 조건을 넣는다.
 * 2026-10-14 search — 사용자가 조회해 달라고 했으면 조건이 없어도 열고 조회한다. "열어줘" 는 지금처럼 열기만.
 */
import { onSearchStore, useMenuStore } from "@shared/libs/Global";
import type { ChatFieldPick, ChatSearchFormData, Menu } from "@shared/type/CommonType";

export interface ChatScreenOpen {
  menuCode: string;
  /** 열고 나서 채울 검색조건. 비면 열기만 */
  paramsPatch?: ChatSearchFormData;
  /** 조건이 없어도 열고 조회한다 — 중앙의 "조회까지" 표시 */
  search?: boolean;
}

export type ScreenOpenResult =
  | { status: "OPENED"; searchApplied?: boolean; searchReason?: string; searchMessage?: string; searchPick?: ChatFieldPick }
  | { status: "NOT_FOUND" }
  | { status: "NO_OPENER" };

/** 검색 패널이 붙기를 기다리는 최대 시간. 화면이 무거워도 보통 1~2초 안에 붙는다 */
const PANEL_WAIT_MS = 4000;
const PANEL_POLL_MS = 100;

/** 최근사용(SN_REC) · 즐겨찾기(SN_FAV) 아래 항목은 같은 메뉴의 복사본이다. 원본으로 연다 */
const SHADOW_PARENTS = new Set(["SN_REC", "SN_FAV"]);

function findMenu(cdMenu: string): Menu | undefined {
  const items = useMenuStore.getState().menuItems || [];
  return (
    items.find(
      (m) => m.cd_menu === cdMenu && !SHADOW_PARENTS.has(m.cd_menu_parent || "")
    ) || items.find((m) => m.cd_menu === cdMenu)
  );
}

function openerOf(): ((...args: unknown[]) => unknown) | undefined {
  const fn = (window as unknown as { onActivate?: unknown }).onActivate;
  return typeof fn === "function" ? (fn as (...args: unknown[]) => unknown) : undefined;
}

/**
 * 검색 패널이 등록되고 칸 이름까지 알게 될 때까지 기다린다. 시간 안에 안 붙으면 false.
 * 패널은 화면이 뜨자마자 정의(sys_formctrl)를 받기 전에 먼저 등록된다. 그때 조건을 넣으면
 * 칸 이름을 하나도 몰라 전부 "없는 항목" 으로 거절한다 (2026-10-02 발주등록 시험). 그래서 칸 이름이 생길 때까지 본다.
 */
async function waitForSearchPanel(menuCode: string): Promise<boolean> {
  const started = Date.now();
  while (Date.now() - started < PANEL_WAIT_MS) {
    const handle = onSearchStore.getState().chatSearchPanels[menuCode];
    if (handle && (handle.getAllowedFieldNames?.() || []).length > 0) return true;
    await new Promise((r) => window.setTimeout(r, PANEL_POLL_MS));
  }
  /* 칸 이름이 끝내 안 생겨도 패널이 있으면 넘긴다 — 조건 없는 화면일 수 있다 */
  return Boolean(onSearchStore.getState().chatSearchPanels[menuCode]);
}

export const aiScreenOpen = {
  /** 메뉴를 열고, 조건이 있으면 채워 조회한다 */
  async run(open: ChatScreenOpen): Promise<ScreenOpenResult> {
    const menu = findMenu(open.menuCode);
    if (!menu) return { status: "NOT_FOUND" };
    const opener = openerOf();
    if (!opener) return { status: "NO_OPENER" };

    const filePath = `./${menu.cd_module}/${menu.nm_file}/${menu.nm_file}`.toUpperCase();
    await opener(menu.cd_menu, menu.nm_menu, menu.cd_module, filePath, menu, (menu as { nm_path?: string }).nm_path || "");

    const patch = open.paramsPatch || {};
    if (Object.keys(patch).length === 0 && !open.search) {
      return { status: "OPENED" };
    }
    if (!(await waitForSearchPanel(menu.cd_menu))) {
      /* 넣을 조건 없이 "조회까지"만이면 — 조회조건이 없는 화면(회사등록 등)은 열면 바로 자료가 나온다. 문구 없이 끝낸다 (2026-10-15) */
      if (Object.keys(patch).length === 0) return { status: "OPENED" };
      return { status: "OPENED", searchApplied: false, searchReason: "NO_ACTIVE_SEARCH_PANEL" };
    }
    const result = await onSearchStore.getState().applySearchParamsAndRun(patch, menu.cd_menu);
    return {
      status: "OPENED",
      searchApplied: result.applied,
      searchReason: result.reason,
      searchMessage: result.message,
      searchPick: result.pick,
    };
  },

  /** 사용자에게 보일 한 줄. 성공이면 빈 문자열 */
  message(result: ScreenOpenResult): string {
    switch (result.status) {
      case "NOT_FOUND":
        return "요청한 메뉴를 열 수 없습니다. 메뉴 권한이 없거나 존재하지 않는 메뉴입니다.";
      case "NO_OPENER":
        return "메뉴를 열 수 있는 화면 상태가 아닙니다.";
      case "OPENED":
        /* 이유별로 가른다 — 뭉뚱그리면 중앙 · 화면 어느 쪽 문제인지 못 가린다 (2026-10-02 발주등록 시험) */
        if (result.searchApplied === false) {
          /* 후보가 여럿이면 챗봇이 번호 버튼을 띄운다 — 오류 문구가 아니다 */
          if (result.searchPick) return "";
          /* 코드 · 콤보 칸을 못 맞춘 경우는 이유를 담은 문구가 온다 */
          if (result.searchMessage) return result.searchMessage;
          switch (result.searchReason) {
            case "NO_ACTIVE_SEARCH_PANEL":
              return "메뉴는 열었지만 이 메뉴에는 조회조건이 없어 조건을 넣지 못했습니다.";
            case "MENU_MISMATCH":
              return "메뉴는 열었지만 활성 화면이 달라 검색조건을 적용하지 않았습니다.";
            case "NO_ALLOWED_PARAMS":
              return "메뉴는 열었지만 그 메뉴 조회조건에 없는 항목이라 넣지 않았습니다.";
            case "SEARCH_NOT_RUN":
              return "메뉴를 열고 검색조건은 넣었지만 조회 실행 함수를 찾지 못했습니다.";
            default:
              return "메뉴는 열었지만 검색조건을 적용하지 못했습니다.";
          }
        }
        return "";
      default:
        return "";
    }
  },
};
